import React, { useState, useMemo } from 'react';
import { getCategorizedArtworks } from '../data/artworkData';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [categorizedArtworks, setCategorizedArtworks] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch categorized artworks on mount
  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getCategorizedArtworks();
      setCategorizedArtworks(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const categories = ['All', ...Object.keys(categorizedArtworks)];

  // Filter artworks based on active category
  const displayedArtworks = useMemo(() => {
    if (activeCategory === 'All') {
      return Object.values(categorizedArtworks).flat();
    }
    return categorizedArtworks[activeCategory] || [];
  }, [activeCategory, categorizedArtworks]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">Art Gallery</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Explore our curated collection of masterfully crafted sculptures and murals,
            handpicked for their artistic excellence.
          </p>
        </div>

        {/* Category Filter Navigation */}
        <div className="sticky top-20 z-30 bg-gray-50/90 backdrop-blur-lg py-6 mb-12 border-b border-gray-100">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 transform ${activeCategory === category
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-200 -translate-y-0.5'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 hover:border-primary-300'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
          {activeCategory !== 'All' && (
            <p className="text-center mt-4 text-sm text-gray-400 font-medium animate-fadeIn">
              Showing {displayedArtworks.length} pieces in <span className="text-primary-600">"{activeCategory}"</span>
            </p>
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {displayedArtworks.map((artwork, index) => (
            <div
              key={`${activeCategory}-${index}`}
              className="group relative overflow-hidden rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white border border-gray-100 animate-fadeIn"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={() => setSelectedImage(artwork)}
            >
              <div className="aspect-[4/5] relative overflow-hidden">
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  loading="lazy"
                />

                {/* Visual Polish: Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500"></div>

                {/* Info Overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out text-white">
                    <h3 className="font-bold text-xl mb-1 drop-shadow-md">{artwork.title}</h3>
                    {artwork.location && artwork.location.trim() !== '' && artwork.location !== 'Location: [To be updated]' && (
                      <p className="text-xs font-medium text-primary-200 flex items-center mb-2 drop-shadow-sm">
                        <span className="mr-1.5 opacity-80">📍</span> {artwork.location}
                      </p>
                    )}
                    <div className="h-0.5 w-0 group-hover:w-12 bg-primary-400 transition-all duration-500"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {displayedArtworks.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200 mt-8">
            <div className="text-5xl mb-4">🎨</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No artworks found</h3>
            <p className="text-gray-500">We're still adding pieces to this category. Check back soon!</p>
          </div>
        )}

        <div className="mt-24 text-center">
          <div className="inline-block p-1 rounded-full bg-gray-100 mb-4 px-4 py-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Masterpieces in {new Date().getFullYear()}</p>
          </div>
          <p className="text-gray-400 italic text-sm">Every piece tells a story, carved with passion.</p>
        </div>
      </div>

      {/* Modal for enlarged image */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-gray-900/95 z-50 flex items-center justify-center p-4 md:p-8 backdrop-blur-sm transition-all duration-300"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-6xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row h-full max-h-[85vh] animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full text-white sm:text-gray-800 sm:bg-gray-100 sm:hover:bg-gray-200 transition-all text-2xl font-light"
              aria-label="Close modal"
            >
              ×
            </button>

            {/* Image Container */}
            <div className="md:w-3/5 lg:w-2/3 bg-gray-50 flex items-center justify-center relative overflow-hidden group/modal">
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain p-4 transition-transform duration-500"
              />
            </div>

            {/* Details Container */}
            <div className="md:w-2/5 lg:w-1/3 p-8 lg:p-12 flex flex-col justify-between bg-white overflow-y-auto">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="h-px w-8 bg-primary-600"></span>
                  <span className="text-xs font-black text-primary-600 uppercase tracking-widest">Exquisite Creation</span>
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-6 leading-tight">{selectedImage.title}</h3>

                <div className="space-y-6 mb-10">
                  {selectedImage.medium && (
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Artistic Medium</span>
                      <p className="text-gray-800 font-medium">{selectedImage.medium}</p>
                    </div>
                  )}
                  {selectedImage.size && (
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Dimensions</span>
                      <p className="text-gray-800 font-medium">{selectedImage.size}</p>
                    </div>
                  )}
                  {selectedImage.location && selectedImage.location.trim() !== '' && selectedImage.location !== 'Location: [To be updated]' && (
                    <div className="bg-primary-50 p-4 rounded-xl border border-primary-100">
                      <span className="text-[10px] font-black text-primary-400 uppercase tracking-widest block mb-1">Current Venue</span>
                      <p className="text-primary-700 font-bold flex items-center">
                        <span className="mr-2">📍</span> {selectedImage.location}
                      </p>
                    </div>
                  )}

                  {selectedImage.description && (
                    <div className="pt-4">
                      <p className="text-gray-600 leading-relaxed italic border-l-4 border-gray-100 pl-4 py-1">
                        "{selectedImage.description}"
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <button
                className="w-full py-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-primary-600 hover:shadow-xl hover:shadow-primary-100 transform active:scale-95 transition-all duration-300 mt-6"
                onClick={() => {
                  window.location.href = '/#contact';
                  setSelectedImage(null);
                }}
              >
                Inquire for Custom Sculptures
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
